import { useEffect, useState } from 'react'
import { Link, Navigate, useSearchParams } from 'react-router'
import type { JSX, ReactNode, SyntheticEvent } from 'react'
import { api, call } from '@/api/client'
import type { ApiFailure } from '@/api/client'
import { LOGO_ACCEPT, LOGO_RULE, logoProblem, putLogo } from '@/api/logo'
import { useMutation } from '@/api/query'
import { useAuth } from '@/auth/context'
import { followHandoff } from '@/auth/handoff'
import { NEXT_PARAM, safeNext } from '@/auth/routes'
import { useBrand } from '@/brand/context'
import { MIN_PASSWORD } from '@/lib/password'
import { domainLabel } from '@/lib/workspace'
import { Button } from '@/ui/Button'
import { Field, TextField } from '@/ui/Field'
import { GoogleButton } from '@/ui/GoogleButton'
import { PasswordField } from '@/ui/PasswordField'
import { InlineError } from '@/ui/states'
import type { FieldProblem } from '@/ui/states'
import { useToast } from '@/ui/toast-context'
import { looksLikeEmail, useBlurValidation } from '@/ui/validation'
import { INVITATION_PARAM } from './AcceptInvitation'
import { PublicPage } from './PublicPage'

type Route = 'email' | 'mobile'

/** The labels the summary uses, so it names a field the way the field does. */
const ORGANISATION_LABEL = 'Organisation name'
const ADDRESS_LABEL = 'Workspace address'
const EMAIL_LABEL = 'Work email'
const MOBILE_LABEL = 'Mobile number'
const PASSWORD_LABEL = 'Password'

/**
 * The codes a refused workspace address comes back under (`app/v1/auth.py`).
 * A 409 carries no `details.fields`, so — as with a taken email — the code is
 * the only thing that names the field; `WORKSPACE_MISMATCH` also carries the
 * address the rule would accept, in `details.suggestion`.
 */
const ADDRESS_CODES: ReadonlySet<string> = new Set([
  'WORKSPACE_MISMATCH',
  'WORKSPACE_RESERVED',
  'WORKSPACE_NEEDS_BUSINESS_EMAIL',
  'SLUG_TAKEN',
])

/**
 * Mirrors `MobileSignupRequest.mobile` in `openapi.json` (`minLength: 8,
 * maxLength: 24`). The allowance over E.164's 15 digits is for the spaces,
 * hyphens and brackets the server strips. Bounded here as well as on the server
 * because a 422 on this screen says only "Request validation failed", and the
 * form has a number, a password and a checkbox on it (WEB-14).
 */
const MOBILE_MIN = 8
const MOBILE_MAX = 24

/**
 * The live availability check (spec: *Workspace address rule*, item 5).
 *
 * `GET /auth/workspace` is asked once the typing pauses — 400 ms, long enough
 * that a word is one request rather than six — and never for fewer than three
 * characters, because the rule refuses those anyway and a hint that says
 * *too short* under every first keystroke is noise. **Advisory**: the submit
 * relies on the server's own 409, so a check that could not be made (the
 * route throttled, the network gone) says nothing rather than something
 * wrong. `idle` covers both that and *nothing to check yet*.
 */
const CHECK_DELAY_MS = 400
const CHECK_MIN_LENGTH = 3

type Check =
  | { status: 'idle' }
  | { status: 'checking' }
  | { status: 'available' }
  | { status: 'unavailable'; message: string; suggestion: string | null }

const IDLE: Check = { status: 'idle' }

function useWorkspaceCheck(address: string, email: string | null): Check {
  const [check, setCheck] = useState<Check>(IDLE)

  useEffect(() => {
    if (address.length < CHECK_MIN_LENGTH) {
      setCheck(IDLE)
      return
    }
    // The last verdict was about a different address, so it goes now rather
    // than when the next one lands — *Available* must not stand under a value
    // nobody has checked. Aborted on the next keystroke as well as on unmount,
    // so an answer to an address that is no longer in the box cannot land on
    // the one that is.
    setCheck({ status: 'checking' })
    const controller = new AbortController()
    const timer = window.setTimeout(() => {
      call(() =>
        api.GET('/api/v1/auth/workspace', {
          params: { query: { address, email } },
          signal: controller.signal,
        }),
      )
        .then((result) => {
          if (controller.signal.aborted) return
          if (!result.ok) {
            setCheck(IDLE)
            return
          }
          setCheck(
            result.data.available
              ? { status: 'available' }
              : {
                  status: 'unavailable',
                  message: result.data.message,
                  suggestion: result.data.suggestion ?? null,
                },
          )
        })
        .catch(() => {
          // `call()` re-throws only an abort, and an aborted check belongs to
          // an address that is no longer in the box.
        })
    }, CHECK_DELAY_MS)
    return () => {
      window.clearTimeout(timer)
      controller.abort()
    }
  }, [address, email])

  return check
}

/**
 * `/signup` — S-D2 screens 01 and 02.
 *
 * **Three ways in, and they are alternatives rather than a set** (§4). An agency
 * owner will press Google; a shop owner in Hyderabad will use their mobile
 * number, and that account has no email address at all — which is why
 * `users.email` is nullable and why this is a toggle rather than two fields.
 *
 * The password rules are shown before the first submit, not after a rejection,
 * and the strength meter says what it can honestly measure — see
 * `lib/password.ts` for why it does not claim to check breach lists.
 *
 * The terms checkbox is now enforced on **both** sides, and S-B12 is why. When
 * this page was written the consent table did not exist and a server-side check
 * would have been a flag nothing stores — worse than an honest client-side gate,
 * because it would look like a record. `public.consent_records` exists now, so
 * the box is sent with the request, the API refuses a signup without it, and
 * three versioned rows are written in the same transaction that creates the
 * account. DPDP requires consent to be **demonstrable**; a checkbox this
 * component enforced alone demonstrates nothing.
 *
 * The client-side check stays, because the two do different jobs: this one puts
 * the message next to the control the person did not tick, and the server's one
 * is what makes the record true.
 *
 * **Two kinds of signup, one form.** With `?invitation=<token>` — the link
 * `AcceptInvitation` offers somebody without an account — the organisation is
 * the one that sent the invitation: no name, no workspace address, no logo,
 * and the request carries `invitation_token` instead of `organisation`. The
 * API creates the account, accepts the invitation and answers `handoff_url`
 * for the organisation's host, which is followed as any sign-in's is. Email
 * only, because an invitation is addressed to a mailbox and the API refuses a
 * mobile account for it; and no Google button, because the Google sign-in
 * carries no token. Nothing about workspaces runs — a colleague at a claimed
 * business domain has no address of their own to open, which is the point.
 */
export function Signup(): JSX.Element {
  const { adopt } = useAuth()
  const brand = useBrand()
  const toast = useToast()
  const [params, setParams] = useSearchParams()
  const invited = safeNext(params.get(NEXT_PARAM))
  const invitationToken = params.get(INVITATION_PARAM)?.trim() || null
  const joining = invitationToken !== null

  const [route, setRoute] = useState<Route>('email')
  const [organisation, setOrganisation] = useState('')
  // The workspace address: `<address>.augrank.com` becomes the organisation's
  // own host (spec: *Signup*). Pre-filled from the email's domain label as the
  // email is typed — `info@augmentrait.com` → `augmentrait` — because that is
  // the one address the rule lets a business email claim, and it stops
  // following the email the moment the person edits it themselves.
  const [address, setAddress] = useState('')
  const [addressEdited, setAddressEdited] = useState(false)
  const [email, setEmail] = useState('')
  const [mobile, setMobile] = useState('')
  const [password, setPassword] = useState('')
  // The logo is optional and is sent *after* the account exists, to
  // `PUT /tenants/{id}/logo` — the organisation has to have an id first. Held
  // as the `File` the picker gave, with a data URL for the preview.
  const [logo, setLogo] = useState<{ file: File; preview: string } | null>(null)
  const [logoError, setLogoError] = useState<string | null>(null)
  const [agreed, setAgreed] = useState(false)
  const [agreementError, setAgreementError] = useState<string | null>(null)

  // Measured on the trimmed value, which is what `onSubmit` sends, and silent
  // until something has been typed — a required field that objects before it is
  // touched is noise.
  const trimmedMobile = mobile.trim()
  const mobileError =
    route === 'mobile' && trimmedMobile !== '' && trimmedMobile.length < MOBILE_MIN
      ? 'Include the country code — at least 8 characters.'
      : undefined

  // AUTH-1: `data-validate="required|email"` on screen 01, shown on blur and
  // then live. The two halves are one message rather than two because they are
  // one question — *is there a usable address in this box* — and a field that
  // says "required" and then "not an address" on the next keystroke reads as
  // two different objections to the same typo.
  const trimmedEmail = email.trim()
  const emailRule = useBlurValidation(
    trimmedEmail === ''
      ? 'Enter the email address you want the account on.'
      : looksLikeEmail(trimmedEmail)
        ? undefined
        : 'That does not look like an email address — check for a typo.',
  )

  // What the live check is asked about: the address as the submit would send
  // it, and the email once it is one — the rule reads the domain off it, and
  // a half-typed address would be refused for its shape rather than answered.
  const candidate = joining ? '' : address.trim().toLowerCase()
  const checkEmail =
    route === 'email' && looksLikeEmail(trimmedEmail) ? trimmedEmail.toLowerCase() : null
  const check = useWorkspaceCheck(candidate, checkEmail)

  // Where a successful signup lands when the API does not hand it off. An
  // invited signup has already accepted its invitation, so the accept page it
  // came from has nothing left to do: the picker shows the organisation it
  // joined (only reached when that workspace is not open yet — otherwise
  // `handoff_url` takes it to the organisation's host). A plain signup that
  // arrived with a `next` keeps it — dropping that person at "check your
  // inbox" would strand whatever they were on the way to.
  const destination = joining
    ? '/organisations'
    : (invited ?? (route === 'mobile' ? '/verify-mobile' : '/check-email'))

  type Organisation = { name: string; address: string }
  const withEmail = useMutation(
    (body: {
      email: string
      password: string
      accepted_terms: boolean
      organisation?: Organisation
      invitation_token?: string
    }) => call(() => api.POST('/api/v1/auth/signup', { body })),
  )
  const withMobile = useMutation(
    (body: {
      mobile: string
      password: string
      accepted_terms: boolean
      organisation: Organisation
    }) => call(() => api.POST('/api/v1/auth/signup/mobile', { body })),
  )

  const pending = withEmail.pending || withMobile.pending
  const failure = withEmail.state.failure ?? withMobile.state.failure
  const refused = refusedFields(failure)

  // AUTH-1/AUTH-2: the field's own message is whichever of the two is live —
  // the server's refusal when there is one, the client's rule otherwise. Never
  // both: they are two verdicts on one value, and the server's is the one that
  // stopped the submit.
  const emailError = refused.email ?? emailRule.shown
  const mobileFieldError = refused.mobile ?? mobileError

  // The server's refusal first, then the live check's; never both. They are
  // two verdicts on the same value, and once a submit has been refused the
  // refusal is the one that matters until the address changes — which clears
  // it (`clearFailures` on the field's change).
  function applySuggestion(suggestion: string): void {
    setAddress(suggestion)
    setAddressEdited(true)
    clearFailures()
  }
  const addressError: ReactNode =
    refused.address !== undefined ? (
      <>
        {refused.address}
        {failure?.suggestion ? (
          <SuggestionButton suggestion={failure.suggestion} onUse={applySuggestion} />
        ) : null}
      </>
    ) : check.status === 'unavailable' ? (
      <>
        {check.message}
        {check.suggestion !== null ? (
          <SuggestionButton suggestion={check.suggestion} onUse={applySuggestion} />
        ) : null}
      </>
    ) : undefined

  function clearFailures(): void {
    withEmail.reset()
    withMobile.reset()
  }

  async function onSubmit(event: SyntheticEvent): Promise<void> {
    event.preventDefault()
    if (!agreed) {
      setAgreementError('Please accept the terms to create an account.')
      return
    }
    setAgreementError(null)
    // Before `setParams` below, so a refused submit does not leave `next`
    // rewritten for a request that never went out.
    if (mobileError !== undefined) return
    // A submit is also a departure from the field, so the email rule is made
    // visible here rather than only on blur — otherwise a form submitted with
    // Enter from the password box sends `priya@gmail` and waits for a 422 to
    // say what this already knows.
    if (route === 'email' && emailRule.error !== undefined) {
      emailRule.onBlur()
      return
    }

    // The destination goes into `next` *before* the request, and that is not a
    // flourish — it is the only ordering that is deterministic. `adopt()` is an
    // urgent state update while a navigation is a router transition, so the
    // instant the session lands `RequireAnonymous` re-renders at `/signup` with
    // an authenticated user and redirects **before** any navigation this
    // component started can commit. That sent every new account past the screen
    // it was meant to see. Setting `next` first makes the guard's own redirect
    // the right one, so there is nothing left to race.
    const updated = new URLSearchParams(params)
    updated.set(NEXT_PARAM, destination)
    setParams(updated, { replace: true })

    // A mobile account is unusable until the number is verified, so the OTP
    // screen is where that signup goes. Both routes end the same way: adopt the
    // identity and let the guard carry us to `destination`.
    const workspace: Organisation = {
      name: organisation.trim(),
      address: address.trim().toLowerCase(),
    }
    const result =
      route === 'mobile'
        ? await withMobile.run({
            mobile: mobile.trim(),
            password,
            accepted_terms: true,
            organisation: workspace,
          })
        : await withEmail.run({
            email: email.trim().toLowerCase(),
            password,
            accepted_terms: true,
            ...(invitationToken !== null
              ? { invitation_token: invitationToken }
              : { organisation: workspace }),
          })
    if (!result.ok) return

    // The logo, now that the organisation has an id to put it on. A failure
    // here must not strand a person whose account already exists: it is said,
    // with where to do it later, and the signup carries on to the inbox screen
    // as if no logo had been chosen. A toast rather than a message beside the
    // picker, because this form is gone the moment the session is adopted.
    if (logo !== null) {
      const tenantId = result.data.session.tenant_id ?? null
      const stored = tenantId === null ? null : await putLogo(tenantId, logo.file)
      if (stored === null || !stored.ok) {
        toast.show({
          kind: 'warning',
          title: 'Your logo was not saved',
          body: `${stored === null ? '' : `${stored.failure.message} `}You can add a logo later in Settings → Branding.`,
        })
      }
    }

    // An email signup lands on `/check-email` here on the apex and the API
    // answers `handoff_url: null` for it; the field is honoured all the same,
    // because the API owns the rule and a signup it does hand off — a route
    // added later — must not be left on a page that says to check an inbox.
    // Only the email response carries the field — a mobile account has no
    // verified organisation to be handed to before its number is confirmed.
    if (followHandoff('handoff_url' in result.data ? result.data.handoff_url : null)) return
    adopt({ user: result.data.user, session: result.data.session })
  }

  // Signing up is the apex's (spec: *Hosts* — `/signup` → `/login` on a
  // tenant host). A workspace address is claimed on `augrank.com`, and an
  // organisation's own login page has no business offering to create another.
  // After the hooks, because it is a render decision and they are not optional.
  if (brand.host === 'tenant') return <Navigate to="/login" replace />

  return (
    <PublicPage
      title={joining ? 'Create your account' : 'Start your 7-day free trial'}
      /*
        AUTH-33, and AUTH-19 closing the gap it was written around.

        The note that stood here said the design's "No card needed to **start**"
        could not be printed, because the trial began at checkout rather than at
        signup — so the drawn sentence promised something the code did not keep.
        The owner closed that on 2026-09-09: the trial now starts here, runs
        seven days, and carries every feature, so the sentence is true and is
        printed. `app/billing/trial.py` is the policy; `app/tenancy/
        organisations.create_organisation` is what starts the clock.
      */
      subtitle={
        joining
          ? 'You have been invited to join an organisation. Use the email address the invitation was sent to.'
          : 'SEO and outreach on autopilot. Seven days free, and no card needed to start.'
      }
      footer={
        <>
          Already have an account?{' '}
          {/* AUTH-37: `.ar-link` explicitly — see the note on `Login.tsx`'s footer. */}
          <Link
            className="ar-link"
            to={invited ? `/login?${NEXT_PARAM}=${encodeURIComponent(invited)}` : '/login'}
          >
            Sign in
          </Link>
        </>
      }
    >
      {/* `method="post"` so a submit with dead JavaScript cannot put this password
          in the query string. Reasoning in full on `Login.tsx`'s form. */}
      <form
        method="post"
        onSubmit={(event) => {
          void onSubmit(event)
        }}
      >
        <div className="ar-card__body ar-stack">
          {joining ? null : (
            <>
              <GoogleButton label="Continue with Google" acceptedTerms={agreed} />

              <div className="app-seg" role="group" aria-label="Sign up with">
                {(['email', 'mobile'] as const).map((option) => (
                  <button
                    key={option}
                    type="button"
                    aria-pressed={route === option}
                    onClick={() => {
                      setRoute(option)
                      clearFailures()
                      emailRule.reset()
                    }}
                  >
                    {option === 'email' ? 'Email' : 'Mobile'}
                  </button>
                ))}
              </div>
            </>
          )}

          {/* AUTH-3/AUTH-4: the summary lists the fields the refusal is about
              and takes focus, so a keyboard or screen-reader user is put at the
              explanation rather than left at whichever control they submitted
              from. `problems` is the same mapping the fields below use. */}
          <InlineError failure={failure} problems={summaryProblems(refused)} takesFocus />

          {joining ? null : (
            <TextField
              label={ORGANISATION_LABEL}
              required
              autoFocus
              autoComplete="organization"
              maxLength={200}
              placeholder="Kavya Interiors"
              value={organisation}
              onChange={(event) => {
                setOrganisation(event.target.value)
                clearFailures()
              }}
            />
          )}

          {joining ? null : (
            <Field
              label={ADDRESS_LABEL}
              required
              help={
                route === 'email'
                  ? "Your organisation's own address: letters, digits and hyphens. A work email claims its own domain's name."
                  : "Your organisation's own address: 3–40 letters, digits and hyphens."
              }
              error={addressError}
              ok={check.status === 'available' ? 'Available' : undefined}
            >
              {(aria) => (
                // The suffix is fixed and drawn beside the box rather than typed
                // into it, so what is entered is the one part that is theirs.
                <div className="ar-input-group">
                  <input
                    className="ar-input"
                    {...aria}
                    autoComplete="off"
                    autoCapitalize="none"
                    spellCheck={false}
                    minLength={3}
                    maxLength={40}
                    pattern="[a-z0-9]([a-z0-9-]{1,38}[a-z0-9])?"
                    placeholder="kavya-interiors"
                    value={address}
                    onChange={(event) => {
                      setAddress(event.target.value)
                      setAddressEdited(true)
                      clearFailures()
                    }}
                  />
                  <span className="ar-input-group__addon">.{brand.baseDomain}</span>
                </div>
              )}
            </Field>
          )}

          {route === 'email' ? (
            <TextField
              label={EMAIL_LABEL}
              type="email"
              required
              autoFocus={joining}
              autoComplete="email"
              placeholder="you@company.in"
              error={emailError}
              /* AUTH-39. No help line. Screen 01 gives this field a label and a
                 placeholder and stops there, and the design's own rationale for
                 the screen is that there are three ways in and none of them
                 should read as work. What the help said — that approvals and
                 alerts go to this address — is true and worth saying, but the
                 place to say it is where somebody is deciding to change it
                 (`/check-email`, Settings › Notifications), not in front of
                 somebody who has not signed up yet. */
              value={email}
              onBlur={emailRule.onBlur}
              onChange={(event) => {
                setEmail(event.target.value)
                // The address follows the email's domain until it is edited
                // by hand; a free-mail address implies nothing and leaves it.
                if (!addressEdited) setAddress(domainLabel(event.target.value) ?? '')
                clearFailures()
              }}
            />
          ) : (
            <TextField
              label={MOBILE_LABEL}
              type="tel"
              required
              autoComplete="tel"
              inputMode="tel"
              maxLength={MOBILE_MAX}
              error={mobileFieldError}
              placeholder="+91 98490 12345"
              help="Include the country code. We text a 6-digit code to confirm it."
              value={mobile}
              onChange={(event) => {
                setMobile(event.target.value)
                clearFailures()
              }}
            />
          )}

          <PasswordField
            label={PASSWORD_LABEL}
            required
            minLength={MIN_PASSWORD}
            autoComplete="new-password"
            error={refused.password}
            value={password}
            onChange={(event) => {
              setPassword(event.target.value)
              clearFailures()
            }}
          />

          {joining ? null : (
            <Field
              label={
                <>
                  Logo <span className="ar-field__optional">(optional)</span>
                </>
              }
              help={LOGO_RULE}
              error={logoError}
            >
              {(aria) => (
                <div className="ar-row">
                  {logo !== null ? (
                    <img
                      className="app-logo-preview"
                      src={logo.preview}
                      alt={`Preview of ${logo.file.name}`}
                    />
                  ) : null}
                  {/* `accept` is a hint to the picker; `logoProblem` checks what
                    came back and the API sniffs the bytes. Cleared after each
                    pick so the same file chosen twice fires again. */}
                  <input
                    {...aria}
                    className="ar-input"
                    type="file"
                    accept={LOGO_ACCEPT}
                    onChange={(event) => {
                      const picked = event.currentTarget.files?.[0]
                      event.currentTarget.value = ''
                      if (picked === undefined) return
                      const problem = logoProblem(picked)
                      if (problem !== null) {
                        setLogo(null)
                        setLogoError(problem)
                        return
                      }
                      setLogoError(null)
                      const reader = new FileReader()
                      reader.onerror = () => {
                        setLogoError('That file could not be read from your computer.')
                      }
                      reader.onload = () => {
                        setLogo({
                          file: picked,
                          preview: typeof reader.result === 'string' ? reader.result : '',
                        })
                      }
                      reader.readAsDataURL(picked)
                    }}
                  />
                  {logo !== null ? (
                    <Button
                      tone="quiet"
                      size="sm"
                      onClick={() => {
                        setLogo(null)
                        setLogoError(null)
                      }}
                    >
                      Remove
                    </Button>
                  ) : null}
                </div>
              )}
            </Field>
          )}

          <div className="ar-field" data-state={agreementError ? 'invalid' : undefined}>
            {/* AUTH-38: body-sm, as screen 01 sets it inline. Three sentences of
                legal text at body size beside a checkbox reads as the form's
                subject rather than as its condition. AUTH-37: `.ar-link` on all
                three, which is also what the mockup writes. */}
            <label className="ar-choice ar-choice--check app-choice--sm">
              <input
                type="checkbox"
                checked={agreed}
                onChange={(event) => {
                  setAgreed(event.target.checked)
                  setAgreementError(null)
                }}
              />
              <span className="ar-choice__box" aria-hidden="true" />
              <span className="ar-choice__text">
                I agree to the{' '}
                <a className="ar-link" href="https://augrank.com/terms">
                  Terms
                </a>{' '}
                and{' '}
                <a className="ar-link" href="https://augrank.com/privacy">
                  Privacy Policy
                </a>
                , including the{' '}
                <a className="ar-link" href="https://augrank.com/dpdp">
                  DPDP notice
                </a>
                .
              </span>
            </label>
            {agreementError ? (
              <span className="ar-field__error">
                {/* AUTH-36 — see `ui/Field.tsx`. Written out here rather than
                    routed through `Field`, because the control this is about is
                    a `<label>` wrapping a checkbox, not an input `Field` owns. */}
                <span aria-hidden="true">✕</span>
                {agreementError}
              </span>
            ) : null}
          </div>

          {/* AUTH-34. Last in the card body, not in a bordered footer — see the
              note on `Login.tsx`'s CTA. */}
          <Button
            tone="primary"
            size="lg"
            block
            type="submit"
            busy={pending}
            busyLabel="Creating account"
            disabled={mobileError !== undefined}
          >
            Create account
          </Button>
        </div>
      </form>
    </PublicPage>
  )
}

/**
 * *Use "augmentrait"* — the address the rule would accept, as one press.
 *
 * Rendered inside the field's error line, from either verdict that carries a
 * suggestion: the live check's `suggestion`, and a submit refused with
 * `WORKSPACE_MISMATCH`. A person told their address must match their email's
 * domain should not have to work out what that means and type it.
 */
function SuggestionButton({
  suggestion,
  onUse,
}: {
  suggestion: string
  onUse: (suggestion: string) => void
}): JSX.Element {
  return (
    <Button
      tone="quiet"
      size="sm"
      onClick={() => {
        onUse(suggestion)
      }}
    >
      Use &ldquo;{suggestion}&rdquo;
    </Button>
  )
}

/**
 * Which field a refused signup is *about*, and what to say under it (AUTH-2).
 *
 * Screen 02 is the whole reason this exists. The API answers a taken address
 * with a 409 and one sentence; the design answers it with that sentence under
 * the email field, the field marked invalid, and the two ways forward from it —
 * because somebody whose address is already registered does not want to be told
 * again, they want to sign in or reset a password they have forgotten.
 *
 * A 409 carries no `details.fields` — only a 422 does — so the code is the only
 * thing that names the field, and this is the table that reads it. Anything not
 * in the table stays where it was: the summary alone, unattributed, which is
 * honest about not knowing rather than guessing at the email field.
 */
function refusedFields(failure: ApiFailure | null): {
  email?: ReactNode
  mobile?: ReactNode
  password?: ReactNode
  address?: ReactNode
} {
  if (!failure) return {}

  if (ADDRESS_CODES.has(failure.code)) {
    // The API's own sentence. On a mismatch it also carries the address it
    // would take instead — `Signup` draws that as a button beside this.
    return { address: failure.message }
  }

  if (failure.code === 'EMAIL_ALREADY_REGISTERED') {
    return {
      email: (
        <>
          An account already uses this email.{' '}
          <Link className="ar-link" to="/login">
            Sign in instead
          </Link>{' '}
          or{' '}
          <Link className="ar-link" to="/forgot-password">
            reset your password
          </Link>
          .
        </>
      ),
    }
  }

  if (failure.code === 'MOBILE_ALREADY_REGISTERED') {
    return {
      mobile: (
        <>
          An account already uses this number.{' '}
          <Link className="ar-link" to="/login">
            Sign in instead
          </Link>{' '}
          or{' '}
          <Link className="ar-link" to="/forgot-password">
            reset your password
          </Link>
          .
        </>
      ),
    }
  }

  // A 422's `loc` is `["body", "<field>"]`. Read from the end, because the
  // prefix differs between a body field and a query one and the last segment is
  // the name either way.
  const found: {
    email?: ReactNode
    mobile?: ReactNode
    password?: ReactNode
    address?: ReactNode
  } = {}
  for (const field of failure.fields) {
    const name = field.loc.at(-1)
    if (name === 'email' && found.email === undefined) found.email = field.msg
    if (name === 'mobile' && found.mobile === undefined) found.mobile = field.msg
    if (name === 'password' && found.password === undefined) found.password = field.msg
    if (name === 'address' && found.address === undefined) found.address = field.msg
  }
  return found
}

/**
 * The same refusal as a list for the summary (AUTH-3).
 *
 * Screen 02's summary is a `<ul>` naming each failing field — *"That email is
 * already registered"*, *"Password is too easy to guess"* — and the field
 * messages below are the detail. The two must not disagree, which is why both
 * are derived from one `refusedFields` call rather than each reading the
 * failure again.
 *
 * The recovery links are dropped here on purpose: they are one offer, and
 * printing them twice on one screen makes neither of them the thing to press.
 */
function summaryProblems(refused: {
  email?: ReactNode
  mobile?: ReactNode
  password?: ReactNode
  address?: ReactNode
}): FieldProblem[] {
  const problems: FieldProblem[] = []
  if (refused.address !== undefined) {
    problems.push({
      label: ADDRESS_LABEL,
      message:
        typeof refused.address === 'string'
          ? refused.address
          : 'That workspace address cannot be used.',
    })
  }
  if (refused.email !== undefined) {
    problems.push({
      label: EMAIL_LABEL,
      message:
        typeof refused.email === 'string' ? refused.email : 'That email is already registered.',
    })
  }
  if (refused.mobile !== undefined) {
    problems.push({
      label: MOBILE_LABEL,
      message:
        typeof refused.mobile === 'string' ? refused.mobile : 'That number is already registered.',
    })
  }
  if (typeof refused.password === 'string') {
    problems.push({ label: PASSWORD_LABEL, message: refused.password })
  }
  return problems
}
